// Shared metadata for CRM communication impulses (supervision + entity detail badges).

import useSWR from 'swr';
import { useMemo } from 'react';
import { useTranslation } from './i18n';

export type ImpulseType =
  | 'BIRTHDAY' | 'NAME_DAY' | 'NO_PURCHASE' | 'PURCHASE_DROP'
  | 'BRAND_DROP' | 'COURTESY' | 'HOLIDAY' | 'LEAD_SLA' | 'DORMANT';

export type EntityImpulse = {
  key?: string;
  impulseType: ImpulseType | string;
  impulseLabel?: string | null;
  severity: 'INFO' | 'WARN' | 'ALERT' | string;
  entityType?: string;
  entityId?: number;
  pohodaId?: string | null;
  contactId?: number;
  entityName?: string | null;
  contactName?: string | null;
  refDate?: string | null;
  params?: Record<string, unknown>;
};

export const IMP_ICONS: Record<string, string> = {
  BIRTHDAY: '🎂', NAME_DAY: '🎉', HOLIDAY: '🗓️',
  NO_PURCHASE: '📉', PURCHASE_DROP: '📉', BRAND_DROP: '🏷️', COURTESY: '👋',
  LEAD_SLA: '🎯', FIRST_CONTACT: '📞', DORMANT: '🕸️',
};

export const IMP_LABEL_KEYS: Record<string, string> = {
  BIRTHDAY: 'supImpBirthday', NAME_DAY: 'supImpNameDay', HOLIDAY: 'supImpHoliday',
  NO_PURCHASE: 'supImpNoPurchase', PURCHASE_DROP: 'supImpPurchaseDrop',
  BRAND_DROP: 'supImpBrandDrop', COURTESY: 'supImpCourtesy', LEAD_SLA: 'supImpLeadSla',
  FIRST_CONTACT: 'supImpFirstContact', DORMANT: 'supImpDormant',
};

export const IMP_SEV_STYLES: Record<string, string> = {
  ALERT: 'bg-rust/15 text-rust-dark',
  WARN: 'bg-ochre/15 text-ochre',
  INFO: 'bg-sky/15 text-sky-dark',
};

/**
 * Resolver zobrazovaného názvu typu impulsu:
 * vlastní label z /supervision/impulse-settings > impulseLabel na řádku > i18n výchozí.
 * Použití: const labelOf = useImpulseLabels(); labelOf('BRAND_DROP') / labelOf(im.impulseType, im.impulseLabel)
 */
export function useImpulseLabels() {
  const { t } = useTranslation();
  const { data } = useSWR<{ impulseType: string; label?: string | null }[]>(
    '/api/user/crm/analytics/supervision/impulse-settings',
  );
  const map = useMemo(
    () => new Map((data ?? []).map(s => [s.impulseType, s.label])),
    [data],
  );
  return (type: string, rowLabel?: string | null) => {
    if (rowLabel?.trim()) return rowLabel.trim();
    const custom = map.get(type);
    if (custom?.trim()) return custom.trim();
    return t(IMP_LABEL_KEYS[type] ?? 'supImpulse');
  };
}
